import { useEffect, useState } from 'react';

/**
 * Direct port of the Android app's `core/ui/TickingCountdown.kt`. The
 * behaviour is identical on purpose: a parent looking at the phone and the
 * web app side by side sees the same numbers move the same way.
 *
 * Smoothly moves a server-reported seconds value by `step` every second
 * on-screen, instead of only visibly changing whenever the next poll
 * happens to land (every 5 s — see `useParentDashboard`). The backend call
 * stays the actual source of truth: every time `serverValue` changes, the
 * displayed value snaps back to it, correcting whatever this ticked to
 * locally in between.
 *
 * `ticking` is whether the shared balance is currently being spent at all
 * (i.e. some device holds the open session) — while false, the displayed
 * value only moves when a fresh server value arrives, it doesn't tick
 * against nothing.
 */
function useTickingSeconds(serverValue: number, ticking: boolean, step: number): number {
  const [displayed, setDisplayed] = useState(serverValue);

  useEffect(() => {
    setDisplayed(serverValue);
  }, [serverValue]);

  useEffect(() => {
    if (!ticking) return;
    const interval = setInterval(() => {
      setDisplayed((current) => Math.max(0, current + step));
    }, 1000);
    return () => clearInterval(interval);
  }, [ticking, step]);

  return displayed;
}

/** The live-ticking-down remaining balance. */
export function useTickingRemainingSeconds(serverRemainingSeconds: number, ticking: boolean): number {
  return useTickingSeconds(serverRemainingSeconds, ticking, -1);
}

/** The live-ticking-up consumed total. Moves in exact lockstep with the remaining one. */
export function useTickingConsumedSeconds(serverConsumedSeconds: number, ticking: boolean): number {
  return useTickingSeconds(serverConsumedSeconds, ticking, 1);
}

/**
 * Splits an already-ticking *combined* remaining total back into its
 * daily/bonus shares, in that draw-down order — the same "daily pot first,
 * then bonus" rule the backend's `debitConsumption`/`liveRemainingByPot`
 * use (see docs/INTERNET_ACCESS.md).
 *
 * Deliberately derived from the one correctly-ticking total rather than
 * ticking each pot down independently: ticking both in parallel would
 * double-count consumption once the daily pot hits zero, since the real
 * drain only ever touches one pot at a time.
 */
export function splitTickingRemaining(
  serverDailyRemainingSeconds: number,
  serverBonusRemainingSeconds: number,
  tickingTotalRemainingSeconds: number,
): { dailyRemainingSeconds: number; bonusRemainingSeconds: number } {
  const consumedSinceSnapshot = Math.max(
    0,
    serverDailyRemainingSeconds + serverBonusRemainingSeconds - tickingTotalRemainingSeconds,
  );
  const dailyRemainingSeconds = Math.max(0, serverDailyRemainingSeconds - consumedSinceSnapshot);
  const consumedBeyondDaily = Math.max(0, consumedSinceSnapshot - serverDailyRemainingSeconds);
  const bonusRemainingSeconds = Math.max(0, serverBonusRemainingSeconds - consumedBeyondDaily);
  return { dailyRemainingSeconds, bonusRemainingSeconds };
}
